import { escapeQueryDots } from 'library/ts/utilities/modules/URL';
import { PTV_API_PATHS_CONVERGED, PTV_API_PATHS_LEGACY, PTV_SERVERS } from 'plugins/tag/plugin_data_service/ts/Application/model/constants/PTVConstants';
import { AdapterId } from 'plugins/tag/plugin_data_service/ts/Application/model/enums/AdapterId';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';

/**
 * @copyright © Paramount Streaming
 * @author Zeeshan Ahmad
 * @description PTV URL builder utility for constructing API and asset URLs.
 * Call setApiMode(adapterId) once during plugin install to select converged or legacy paths.
 */
export class PTVUrlBuilder {

	private static apiPaths: typeof PTV_API_PATHS_CONVERGED | typeof PTV_API_PATHS_LEGACY = PTV_API_PATHS_CONVERGED;

	/**
	 * Sets the active API path mode based on the resolved adapter ID.
	 * Called once during plugin install before any URLs are built.
	 * @param {string} adapterId - The active adapter ID (e.g. 'pluto' or 'pluto_legacy')
	 */
	static setApiMode( adapterId: string ): void {
		this.apiPaths = adapterId === AdapterId.PLUTO_LEGACY ? PTV_API_PATHS_LEGACY : PTV_API_PATHS_CONVERGED;
	}

	/**
	 * Returns appropriate stitcher server url to fetch HLS content
	 * @param {IParamsVO} paramsVO
	 * @returns {string}
	 */
	static getStitcherServer( paramsVO: IParamsVO ): string {
		// Strip trailing slash — createAssetURL appends /v2/... directly; a trailing slash produces //v2/...
		return ( paramsVO?.STITCHER?.servers?.stitcher ?? PTV_SERVERS.stitcher ).replace( /\/$/, '' );
	}

	/**
	 * Returns appropriate stitcher server url to fetch DASH content
	 * @param {IParamsVO} paramsVO
	 * @returns {string}
	 */
	static getStitcherDashServer( paramsVO: IParamsVO ): string {
		// Strip trailing slash — createAssetURL appends /v2/... directly; a trailing slash produces //v2/...
		// Fall back to servers.stitcher when stitcherDash is absent — converged playout-gateway uses a single
		// base URL for both HLS and DASH; platforms with separate DASH hosts pass stitcherDash explicitly.
		return ( paramsVO?.STITCHER?.servers?.stitcherDash
			?? paramsVO?.STITCHER?.servers?.stitcher
			?? PTV_SERVERS.stitcherDash ).replace( /\/$/, '' );
	}

	/**
	 * Returns appropriate concierge url to setup DRM
	 * @param {IParamsVO} paramsVO
	 * @returns {string}
	 */
	static getConciergeServer( paramsVO: IParamsVO ): string {
		return paramsVO?.STITCHER?.servers?.concierge ?? PTV_SERVERS.concierge;
	}

	/**
	 * @description Returns a complete asset URL for stitcher playback.
	 *
	 * Server selection is inferred from the stitched path extension rather than
	 * `paramsVO.STITCHER.format`, so paths selected by the DSP (e.g. DASH from
	 * `window.stitcherPaths`) always route to the correct stitcher host regardless
	 * of what the site originally passed as `format`.
	 *
	 * The streaming-url API returns paths with a leading `/v2` prefix; synthesized paths
	 * do not include it. Both are handled here so callers do not need to normalize.
	 *
	 * @see https://paramount.atlassian.net/wiki/spaces/STC/pages/603947388/Streaming+URL+API+for+Convergence
	 * @see https://paramount.atlassian.net/wiki/spaces/STC/pages/626429706/Streaming+URL+Configurations+for+Convergence
	 * @see https://paramount.atlassian.net/wiki/spaces/PPS/pages/1196425413/Pluto+TV+Playback+Ad+Integration+in+Smart+Tag#GDPR-Consent-(App-Responsibility)
	 * @param {IParamsVO} paramsVO
	 * @param {string} stitchedPath - Stitcher path, with or without leading `/v2`
	 * @returns {string}
	 */
	static createAssetURL( paramsVO: IParamsVO, stitchedPath: string ): string {
		// .mpd extension → DASH stitcher; anything else → HLS stitcher.
		const server = stitchedPath.includes( '.mpd' )
			? this.getStitcherDashServer( paramsVO )
			: this.getStitcherServer( paramsVO );
		const stitcherParams = paramsVO?.STITCHER?.stitcherParams ?? '';
		// Streaming-url API paths carry a /v2 prefix; synthesized paths do not — avoid double /v2.
		const normalizedPath = stitchedPath.startsWith( '/v2' ) ? stitchedPath : `/v2${stitchedPath}`;
		// token parameters (JWT, Access Token) are added to the query string by the Blackbird plugin.
		// includeExtendedEvents=true instructs AdPod to populate extendedEvents.omsdkEvents in session.json,
		// which Blackbird's OM SDK plugin requires to load the OMID verification script (PLBCK-4749).
		const url = `${server}${normalizedPath}?${stitcherParams}&includeExtendedEvents=true`;
		return this.#escapeQueryDots( this.#appendPrivacyParams( url, paramsVO ) );
	}

	/**
	 * @description Percent-encodes literal dots in the query string as `%2E` so Shaka 4.16+
	 * extension sniffing resolves `mpd`/`m3u8` correctly (GPP consent strings carry dots,
	 * e.g. `gpp=DBABLA~BVQqAAAAAABY.QA`). Delegates to the shared `escapeQueryDots` —
	 * see its TSDoc for the full root cause (Shaka 4000 UNABLE_TO_GUESS_MANIFEST_TYPE →
	 * avia 2103).
	 *
	 * @param {string} url - Fully-built stitcher URL
	 * @returns {string} URL with query-string dots percent-encoded; path untouched
	 */
	static #escapeQueryDots( url: string ): string {
		return escapeQueryDots( url );
	}

	/**
	 * @description Appends IAB privacy and consent query params to a stitcher URL.
	 *
	 * The three established signals (gdpr, gdprConsent, usPrivacy) keep their fixed
	 * camelCase→snake_case translation. Any additional keys on STITCHER.privacy are
	 * forwarded verbatim, so the host can add new signals (e.g. gpp, gpp_sid, is_lat)
	 * without a Smart Tag release — provided the keys are named exactly as the stitcher
	 * query string expects.
	 *
	 * @param {string} url - Base stitcher URL to append to
	 * @param {IParamsVO} paramsVO
	 * @returns {string}
	 */
	static #appendPrivacyParams( url: string, paramsVO: IParamsVO ): string {
		const {
			gdpr,
			gdprConsent,
			usPrivacy,
			...rest
		} = paramsVO?.STITCHER?.privacy ?? {};
		// gdpr is 0|1 — !== undefined guards against silently dropping gdpr=0 (not applicable).
		if ( gdpr !== undefined ) { url += `&gdpr=${gdpr}`; }
		if ( gdprConsent ) { url += `&gdpr_consent=${encodeURIComponent( gdprConsent )}`; }
		if ( usPrivacy ) { url += `&us_privacy=${encodeURIComponent( usPrivacy )}`; }
		// Passthrough for any additional host-supplied signals; keys forwarded as-is.
		for ( const [ key, value ] of Object.entries( rest ) ) {
			if ( value === undefined || value === null ) { continue; }
			url += `&${encodeURIComponent( key )}=${encodeURIComponent( String( value ) )}`;
		}
		return url;
	}

	/**
	 * @description Builds Widevine DRM license URL from the Concierge service.
	 * @see https://paramount.atlassian.net/wiki/spaces/PLTCONTENT/pages/258409590/Service+Concierge
	 * @param {IParamsVO} paramsVO
	 * @returns {string}
	 */
	static buildWidevineUrl( paramsVO: IParamsVO ): string {
		return `${this.getConciergeServer( paramsVO )}${this.apiPaths.widevine}`;
	}

	/**
	 * @description Builds PlayReady DRM license URL from the Concierge service.
	 * @see https://paramount.atlassian.net/wiki/spaces/PLTCONTENT/pages/258409590/Service+Concierge
	 * @param {IParamsVO} paramsVO
	 * @returns {string}
	 */
	static buildPlayReadyUrl( paramsVO: IParamsVO ): string {
		return `${this.getConciergeServer( paramsVO )}${this.apiPaths.playready}`;
	}

	/**
	 * @description Builds FairPlay DRM license URL from the Concierge service.
	 * License requests require `Authorization: Bearer {jwt}` — injected by Blackbird's
	 * `SessionjsonPlugin.handleKeyRequest` (matched via `isFpLicenseRequest`).
	 * `IDrmConfig.fairplay.header` is set defensively; the hook is authoritative.
	 *
	 * @see https://paramount.atlassian.net/wiki/spaces/PLTCONTENT/pages/258409590/Service+Concierge
	 * @param {IParamsVO} paramsVO
	 * @returns {string}
	 */
	static buildFairPlayLicenseUrl( paramsVO: IParamsVO ): string {
		return `${this.getConciergeServer( paramsVO )}${this.apiPaths.fairplayLicense}`;
	}

	/**
	 * @description Builds FairPlay application certificate URL from the Concierge service.
	 *
	 * Cert endpoint path is `/v1/cert/fp` (license endpoint is `/v1/fp`). JWT is embedded
	 * as a `?jwt=` query parameter belt-and-suspenders alongside the Authorization: Bearer
	 * header added by Blackbird's `SessionjsonPlugin.handleKeyRequest` (matched via
	 * `isFpCertRequest`).
	 *
	 * @see https://paramount.atlassian.net/wiki/spaces/PLTCONTENT/pages/258409590/Service+Concierge
	 * @param {IParamsVO} paramsVO
	 * @returns {string}
	 */
	static buildFairPlayCertUrl( paramsVO: IParamsVO ): string {
		return `${this.getConciergeServer( paramsVO )}${this.apiPaths.fairplayCert}?jwt=${paramsVO?.STITCHER?.token}`;
	}
}